'use client'

import NotificationHub from '@/components/notifications/notification-hub'
import { cn } from '@/lib/utils'
import { ChevronRight, PlusIcon, UserIcon, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'
import { useState } from 'react'
import ProfileDropdown from '../common/profile-dropdown'
import Bars3Icon from '../icons/bars-3'
import ChevronDown from '../icons/chevron-down'
import HelmetIcon from '../icons/helmet-icon'
import GridFinderLogo from '../icons/logos/grid-finder-new'
import TwoPeopleIcon from '../icons/two-people-icon'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet'

function MobileNavbarTrigger() {
  return (
    <div className="grid h-11 w-11 place-items-center rounded-sm border border-transparent transition-all hover:border-neutral-blue-600">
      <UserIcon className="size-6 stroke-white" />
    </div>
  )
}

function MobileNavbar({
  handleTracking,
  session,
  pathname,
  canManageCommunity,
}) {
  return (
    <div className="flex items-center gap-3 desktop-sm:hidden">
      <ProfileDropdown
        customTrigger={<MobileNavbarTrigger />}
        session={session}
      />
      <MobileNavDropDowns
        handleTracking={handleTracking}
        session={session}
        pathname={pathname}
        canManageCommunity={canManageCommunity}
      />
    </div>
  )
}

export default function MainNavbar({
  session = null,
  featuredGames = [],
  inboxEnabled = false,
}) {
  const [gamesOpen, setGamesOpen] = useState(false)
  const ownedCommunityCount = session?.user?.communityCounts?.owned || 0
  const adminCommunityCount = session?.user?.communityCounts?.admin || 0
  const canManageCommunity = ownedCommunityCount > 0 || adminCommunityCount > 0

  const pathname = usePathname()
  const router = useRouter()

  const [, activeTabName = ''] = pathname.split('/')

  function handleTracking(event, data, url) {
    // mixpanel.track(event, data)
    router.push(url)
  }

  return (
    <div className="top-0 z-20 col-span-full flex min-h-[70px] flex-col bg-neutral-blue-800 sm:sticky">
      {session && (
        <div className="hidden w-full bg-neutral-blue-900 py-1 sm:flex">
          <div className="screen-grid mx-auto flex w-full items-center justify-end gap-4 text-nowrap">
            <Link
              prefetch={false}
              href="/profile-manager/driver/calendar"
              className="flex items-center gap-1 text-xs hover:opacity-80"
            >
              <HelmetIcon className="h-4 w-4 stroke-white" />
              Driver Dashboard
            </Link>
            {canManageCommunity ? (
              <Link
                prefetch={false}
                href="/manage"
                className="flex items-center gap-1 text-xs hover:opacity-80"
              >
                <TwoPeopleIcon className="h-4 w-4 stroke-white stroke-2" />
                Manage Community
              </Link>
            ) : (
              <Link
                prefetch={false}
                href="/manage"
                className="flex items-center gap-1 text-xs hover:opacity-80"
              >
                <PlusIcon className="h-4 w-4 stroke-white stroke-2" />
                Create Community
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="screen-grid w-fill flex-1 items-center">
        <div className="col-span-full flex w-full flex-1 justify-between">
          <div className="flex shrink-0 items-center gap-8 desktop-md:gap-20">
            <Link href="/" prefetch={false} className="shrink-0">
              <GridFinderLogo className="w-[143px] fill-white" />
            </Link>
            <div className="hidden items-center gap-6 text-p-2 font-semibold desktop-sm:flex desktop-md:gap-10">
              <Link
                prefetch={false}
                href="/sim-racing-events"
                className="relative transition-all duration-300 ease-in-out hover:opacity-80"
              >
                Racing
                {activeTabName === 'sim-racing-events' && (
                  <div className="bg-gf-gradient absolute top-7 h-1 w-full" />
                )}
              </Link>
              <Link
                prefetch={false}
                href="/sim-racing-liveries"
                className="relative transition-all duration-300 ease-in-out hover:opacity-80"
              >
                Liveries
                {activeTabName === 'sim-racing-liveries' && (
                  <div className="bg-gf-gradient absolute top-7 h-1 w-full" />
                )}
              </Link>
              <DropdownMenu
                modal={false}
                open={gamesOpen}
                onOpenChange={setGamesOpen}
              >
                <DropdownMenuTrigger className="cursor-pointer">
                  <div className="group flex items-center gap-1">
                    <p
                      className={cn(
                        'text-p-2! font-semibold! group-hover:opacity-80',
                        { 'opacity-80': gamesOpen },
                      )}
                    >
                      Games
                    </p>
                    <ChevronDown
                      className={cn(
                        'size-5 stroke-white stroke-2 transition-all duration-150 ease-in-out group-hover:opacity-80',
                        {
                          'rotate-180 opacity-80': gamesOpen,
                        },
                      )}
                    />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {featuredGames.map((g) => (
                    <DropdownMenuItem
                      onClick={() =>
                        handleTracking(
                          'Navbar Click',
                          { type: 'games', game: g.name },
                          `/sim-racing-games/${g.name}`,
                        )
                      }
                      key={g.name}
                      className="hover:cursor-pointer"
                    >
                      <Image
                        src={g.cardImgUrl}
                        className="mr-2 inline-block max-h-8 w-auto rounded-sm"
                        alt={g.nicename}
                        width="40"
                        height="600"
                      />
                      <p>{g.nicename}</p>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem
                    onClick={() =>
                      handleTracking(
                        'Navbar Click',
                        { type: 'games' },
                        '/sim-racing-games',
                      )
                    }
                    className="flex items-center gap-1 hover:cursor-pointer"
                  >
                    <p>View all games</p>
                    <ChevronRight className="size-3 stroke-white stroke-[3px]" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link
                prefetch={false}
                href="/sim-racing-communities"
                className="relative transition-all duration-300 ease-in-out hover:opacity-80"
              >
                Communities
                {activeTabName === 'sim-racing-communities' && (
                  <div className="bg-gf-gradient absolute top-7 h-1 w-full" />
                )}
              </Link>
              <Link
                prefetch={false}
                href="/casting"
                className="relative transition-all duration-300 ease-in-out hover:opacity-80"
              >
                Casting
                {activeTabName === 'casting' && (
                  <div className="bg-gf-gradient absolute top-7 h-1 w-full" />
                )}
              </Link>
              <Link
                prefetch={false}
                href="/news"
                className="relative transition-all duration-300 ease-in-out hover:opacity-80"
              >
                News
                {activeTabName === 'news' && (
                  <div className="bg-gf-gradient absolute top-7 h-1 w-full" />
                )}
              </Link>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-3">
            <NotificationHub
              enabled={inboxEnabled}
              userId={session?.user?.id}
              canManage={canManageCommunity}
            />
            {session ? (
              <div className="hidden desktop-sm:flex">
                <ProfileDropdown session={session} />
              </div>
            ) : (
              <>
                <Button
                  aria-label="Login / Register"
                  onClick={() =>
                    handleTracking(
                      'Navbar Click',
                      { type: 'login' },
                      `/auth/login?redirectUrl=${pathname}`,
                    )
                  }
                  className="h-[44px]! rounded-sm! border! border-neutral-blue-600! bg-neutral-blue-800! text-p-1! font-semibold! text-white! hover:border-shades-0! hover:bg-shades-0! group shrink-0 px-2 py-0 transition-all duration-300 ease-in-out sm:px-6"
                >
                  <span className="group-hover:text-black! flex flex-col leading-4 transition-all ease-in-out sm:block sm:leading-normal">
                    <span>Login</span>
                    <span className="hidden sm:inline"> / </span>
                    <span>Register</span>
                  </span>
                </Button>
                <Button
                  onClick={() =>
                    handleTracking(
                      'Navbar Click',
                      { type: 'create' },
                      `/auth/login?redirectUrl=/create`,
                    )
                  }
                  className="hidden! h-[44px]! rounded-sm! border! border-neutral-blue-600! bg-neutral-blue-700! text-p-1! font-semibold! text-white! hover:border-shades-0! hover:bg-shades-0! hover:text-black! tablet:block! transition-all duration-300 ease-in-out"
                >
                  Create Community
                </Button>
              </>
            )}

            {session ? (
              <MobileNavbar
                handleTracking={handleTracking}
                session={session}
                pathname={pathname}
                canManageCommunity={canManageCommunity}
              />
            ) : (
              <div className="desktop-sm:hidden">
                <MobileNavDropDowns
                  handleTracking={handleTracking}
                  pathname={pathname}
                  canManageCommunity={false}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function MobileNavDropDowns({
  handleTracking,
  session,
  pathname = '',
  canManageCommunity = false,
}) {
  const [open, setOpen] = useState(false)

  function routeMatches(path) {
    return pathname === path || pathname.startsWith(`${path}/`)
  }

  const navItems = [
    { label: 'Racing', path: '/sim-racing-events', type: 'racing' },
    { label: 'Liveries', path: '/sim-racing-liveries', type: 'liveries' },
    { label: 'Games', path: '/sim-racing-games', type: 'games' },
    {
      label: 'Communities',
      path: '/sim-racing-communities',
      type: 'communities',
    },
    { label: 'Casting', path: '/casting', type: 'casting' },
    { label: 'News', path: '/news', type: 'news' },
  ]

  const communityAction = canManageCommunity
    ? { label: 'Manage Community', path: '/manage', type: 'manage' }
    : {
        label: 'Create Community',
        path: session ? '/manage' : '/auth/login?redirectUrl=/create',
        type: 'create',
      }

  function onNavigate(type, path) {
    setOpen(false)
    handleTracking('Navbar Click', { type }, path)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Open menu"
          className="grid h-11 w-11 place-items-center rounded-sm border border-transparent transition-all hover:border-neutral-blue-600"
        >
          <Bars3Icon className="w-8 stroke-white" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        showClose={false}
        className="flex h-dvh w-[min(86vw,22rem)] max-w-none flex-col gap-0 rounded-none border-neutral-blue-700 bg-neutral-blue-900 p-0 text-shades-0"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>
        <div className="flex h-full flex-col pt-[max(16px,env(safe-area-inset-top))]">
          <div className="flex items-center justify-between border-b border-neutral-blue-700 px-5 pb-4">
            <p className="text-h-6 font-semibold text-shades-0">Menu</p>
            <SheetClose asChild>
              <button
                type="button"
                aria-label="Close menu"
                className="grid h-11 w-11 place-items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 transition-all hover:border-neutral-blue-500"
              >
                <X className="size-5 stroke-shades-0" />
              </button>
            </SheetClose>
          </div>

          <nav className="flex flex-1 flex-col px-4 py-4">
            {navItems.map((item) => (
              <SheetClose asChild key={item.path}>
                <button
                  type="button"
                  onClick={() => onNavigate(item.type, item.path)}
                  className={cn(
                    'mb-1 flex min-h-12 w-full items-center rounded-sm border border-transparent px-4 py-3 text-left text-p-2 font-semibold transition-all',
                    routeMatches(item.path)
                      ? 'border-neutral-blue-700 bg-neutral-blue-800 text-shades-0'
                      : 'text-neutral-blue-100 hover:border-neutral-blue-700 hover:bg-neutral-blue-800',
                  )}
                >
                  {item.label}
                </button>
              </SheetClose>
            ))}

            <div className="mt-2 h-px w-full bg-neutral-blue-700" />

            <div className="mt-auto pb-[max(12px,env(safe-area-inset-bottom))] pt-4">
              <SheetClose asChild>
                <button
                  type="button"
                  onClick={() =>
                    onNavigate(communityAction.type, communityAction.path)
                  }
                  className="w-full rounded-sm border border-neutral-blue-600 bg-neutral-blue-800 px-4 py-3.5 text-left text-p-2 font-semibold text-shades-0 transition-all hover:border-shades-0 hover:bg-shades-0 hover:text-neutral-blue-900"
                >
                  {communityAction.label}
                </button>
              </SheetClose>
            </div>
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  )
}
